import moment from "moment";
import { InnodomDateFormats } from "@common/util/dateFormats";
import {
  ATTR_CONFIDENCE,
  ATTR_ID_REF,
  ATTR_KEY,
  TAG_CONTENT,
  TAG_DATAPOINT,
  TAG_META,
} from "../constants";
import { ConfidenceClass, getConfidenceClass } from "../Document/getConfidenceClass";
import {
  ATTR_COL,
  ATTR_COLSPAN,
  ATTR_ID,
  ATTR_MAX_X,
  ATTR_MAX_Y,
  ATTR_MIN_X,
  ATTR_MIN_Y,
  ATTR_PAGE,
  ATTR_ROW,
  ATTR_ROWSPAN,
  TAG_BLOCK,
  TAG_COL,
  TAG_IMAGE,
  TAG_MATH,
  TAG_ROW,
  TAG_TABLE,
  TAG_TEXT,
} from "../Document/InnodomContent/constants";
import {
  Block,
  ContentType,
  DataPointValue,
  ImageBlock,
  MathBlock,
  NonGroupTaxonomyEntry,
  State,
  TableBlock,
  TableCellBlock,
  TaxonomyEntry,
  TaxonomyEntryType,
  TextBlock,
} from "../types";
import { newDefaultValue } from "./util";
import { GetHighlightedText } from "../Document/getHighlightedText";
import { WorkbenchTaxonomy } from "../../../../common/api/v1.1/types/taxonomy";

interface IExtractedKeysAndIndexes {
  taxonomyEntry: TaxonomyEntry;
  groupIndex: number | null;
  groupKey: string | null;
  index: number | null;
  key: string;
}
/**
 * Given a dot-separated data point key, extracts the data point key, group key, and both indexes.
 * Note: keys are extracted in lower case format for case-insensitive matching.
 * @param inputKey string The input string.
 * @return IExtractedKeysAndIndexes Returns up to the four parts of the key.
 */
export const extractKeysAndIndexes = (inputKey: string, taxonomy: TaxonomyEntry[]): IExtractedKeysAndIndexes => {
  const keySplit = inputKey.toLocaleLowerCase().split(".");
  let groupKey = null;
  let groupIndex = null;
  let key = "";
  let index = null;
  let taxonomyEntry;
  if (keySplit.length < 1 || keySplit.length > 4) {
    throw new Error(`Invalid data point key: "${inputKey}".`);
  }

  // various split lenghts:
  // point                            -> len = 1
  // group.point  or point.1          -> len = 2
  // group.point.1  or group.1.point  -> len = 3
  // group.1.point.1                  -> len = 4

  if (keySplit.length === 1) {
    key = keySplit[0];
    taxonomyEntry = taxonomy.find(x => x.id.toLocaleLowerCase() === key)
  } else if (keySplit.length === 2) {
    key = keySplit[0];
    // assumes point.1
    index = +keySplit[1];

    // case group.point
    if (isNaN(index)) {
      groupKey = keySplit[0];
      
      // maybe a group is expected
      const groupEntry = taxonomy.find(x => x.id.toLocaleLowerCase() === groupKey);
      if (groupEntry.multiple) {
        groupIndex = 1;
      }
      key = keySplit[1];

      taxonomyEntry = groupEntry.children.find(x => x.id.toLocaleLowerCase() === key);
      // maybe a point index is expected
      if (taxonomyEntry.multiple) {
        index = 1;
      } else {
        index = null;
      }
    // case point.1
    } else {
      taxonomyEntry = taxonomy.find(x => x.id.toLocaleLowerCase() === keySplit[0])
    }
  } else if (keySplit.length === 3) {
    groupKey = keySplit[0];
    groupIndex = +keySplit[1];

    if (isNaN(groupIndex)) {
      // case group.point.1 
      // the group is expected to be multiple but is not.. inject groupIndex = 1
      const groupEntry = taxonomy.find(x => x.id.toLocaleLowerCase() === groupKey);
      if (groupEntry.multiple) {
        groupIndex = 1;
      } else {
        groupIndex = null;
      }
      key = keySplit[1];
      index = parseInt(keySplit[2], 0);
      taxonomyEntry = groupEntry.children.find(x => x.id.toLocaleLowerCase() === key);
      if (isNaN(index)) {
        throw new Error(`Invalid data point key: "${inputKey}".`);
      }

    } else {
      // case group.1.point
      groupKey = keySplit[0]
      key = keySplit[2];
      const groupEntry = taxonomy.find(x => x.id.toLocaleLowerCase() === groupKey);
      taxonomyEntry = groupEntry.children.find(x => x.id.toLocaleLowerCase() === key);
      if (taxonomyEntry.multiple) {
        index = 1;
      }
    }
  } else {
    groupKey = keySplit[0];
    const groupEntry = taxonomy.find(x => x.id.toLocaleLowerCase() === groupKey);
    key = keySplit[2];
    taxonomyEntry = groupEntry.children.find(x => x.id.toLocaleLowerCase() === key);
    groupIndex = parseInt(keySplit[1], 10);
    index = parseInt(keySplit[3], 10);
    if (isNaN(groupIndex) || isNaN(index)) {
      throw new Error(`Invalid data point key: "${inputKey}".`);
    }
  }
  return {
    taxonomyEntry,
    groupIndex,
    groupKey,
    index,
    key,
  };
};

/**
 * For a given <inno:datapoint> element, we map to our in-memory DataPointValue model.
 */
// tslint:disable-next-line: cyclomatic-complexity
const extractDataPointValueFromNode = (
  node: Element,
  blocks: State["blocks"],
  taxonomy: TaxonomyEntry[],
  xmlDoc: Document,
): DataPointValue<string> => {
  let confidence = parseFloat(node.getAttribute(ATTR_CONFIDENCE) || "");
  if (isNaN(confidence)) {
    confidence = 0;
  }
  confidence = Math.max(0, Math.min(1, confidence));
  const idRef = node.getAttribute(ATTR_ID_REF) || "";
  const rawKey = (node.getAttribute(ATTR_KEY) || "").trim().toLowerCase();
  const keysAndIndexes = extractKeysAndIndexes(rawKey, taxonomy);

  /*
  console.log(rawKey);
  if (keysAndIndexes.groupKey) {
    const entry = taxonomy.taxonomyObject.find(entry => entry.id === keysAndIndexes.groupKey);
    if (!entry) {
      throw new Error(`Datapoint "${keysAndIndexes.groupKey}" is not in the taxonomy.`);
    }
    if (entry.type !== TaxonomyEntryType.Group) {
      throw new Error(`Datapoint "${keysAndIndexes.groupKey}" is not a group.`);
    }
    /*if (!entry.dataPoints.includes(keysAndIndexes.key)) {
      throw new Error(
        `Datapoint "${keysAndIndexes.key}" is not in group "${keysAndIndexes.groupKey}".`,
      );
    }
  } else {

    const entry = taxonomy.taxonomyObject.find(entry => entry.id === keysAndIndexes.key);
    if (!entry) {
      throw new Error(`Datapoint "${keysAndIndexes.key}" is not in the taxonomy.`);
    }
    //const entry = taxonomy[keysAndIndexes.key];
    if (entry.type === TaxonomyEntryType.Group) {
      throw new Error(`Data ${keysAndIndexes.key} is a group, not a data point.`);
    }
    if (entry.groups.length > 0 && !entry.groups.includes("")) {
      throw new Error(`Datapoint ${rawKey} is missing its group identifier.`);
    }
  }

  const valueKey = buildValueKey(
    keysAndIndexes.groupKey,
    keysAndIndexes.groupKey && taxonomy.taxonomyObject.find(x => x.id === keysAndIndexes.groupKey).multiple
      ? keysAndIndexes.groupIndex
      : null,
    keysAndIndexes.key,
    taxonomy.taxonomyObject.find(x => x.id === keysAndIndexes.key).multiple ? keysAndIndexes.index : null,
  );*/
  /*const el = xmlDoc.getElementById(idRef);
  const textContent = (el.textContent || "").trim();*/
  let textContent = typeof node.textContent === "string" ? node.textContent.trim() : "";
  // handle pointer types since we need to lookup the value in the document
  if (keysAndIndexes.taxonomyEntry.type === TaxonomyEntryType.Pointer) {
    if (idRef !== null && idRef in blocks) {
      const contentBlock = blocks[idRef];
      switch (contentBlock.type) {
        case ContentType.Table:
        case ContentType.Text:
          textContent = contentBlock.text;
          break;
        case ContentType.Image:
          textContent = contentBlock.text;
          break;
        case ContentType.Math:
          textContent = contentBlock.text;
          break;
      }
    } else {
      const el = xmlDoc.getElementById(idRef);
      if (!el) {
        textContent = "Table selected";
      } else {
        textContent = (el.textContent || "").trim();
      }
    }
  }

  return {
    confidence,
    idRef: idRef.length > 0 ? idRef : null,
    taxonomyKey: rawKey,
    value: textContent,
    valueKey: rawKey,
    valueRaw: textContent,
  };
};

/**
 * Maps the contents of the <inno:metadata> nodes to an object where the keys are the data point
 * keys and the values are the extracted models.
 * @param meta Element The <inno:metadata> element.
 * @return Returns the extract models as values in an object, where the keys are the data point keys
 */
const mapNodesToDataPoints = (
  meta: Element,
  taxonomy: TaxonomyEntry[],
  rawBlocks: State["blocks"],
  xmlDoc: Document,
): [State["blocks"], State["values"]] => {
  const blocks = { ...rawBlocks };
  const values: State["values"] = {};
  const dataPointNodes = Array.from(meta.getElementsByTagName(TAG_DATAPOINT));

  for (const dataPointNode of dataPointNodes) {
    const key = (dataPointNode.getAttribute(ATTR_KEY) || "").trim().toLowerCase();
    if (key.length > 0) {
      try {
        let value = extractDataPointValueFromNode(dataPointNode, blocks, taxonomy, xmlDoc);
        if (value.idRef) {
          if (!(value.idRef in blocks)) {
            value = { ...value, idRef: null };
          } else {
            blocks[value.idRef].references.push(value.valueKey);
          }
        }
        values[value.valueKey] = value;
      } catch (err) {
        console.error(err);
      }
    }
  }
  return [blocks, values];
};

/**
 * A reducer function producer that maps the incoming taxonomy to our data points model.
 * @param taxonomy ITaxonomy The input taxonomy.
 * @return Returns a reducer function thatproduces the data point model.
 */
/*
const taxonomyToDataPointMap = (taxonomy: State["taxonomy"]) => (
  prev: State["dataPoints"],
  key: string,
): State["dataPoints"] => {
  const current = taxonomy[key];
  if (current.type === TaxonomyEntryType.Group) {
    // skip this group definition...we will build it when we find our first data point that
    // belongs to the group
    return prev;
  }
  const newEntry: IBaseDataPoint = {
    isGroup: false,
    key,
    label: current.question || current.description || key,
    multiple: typeof current.multiple === "boolean" ? current.multiple : true,
    type: current.type,
  };
  let foundGroup = false;
  if (Array.isArray(current.groups)) {
    for (const groupElement of current.groups) {
      if (typeof groupElement !== "string") {
        continue;
      }
      const group = groupElement.trim();
      if (group === "") {
        // an empty group name indicates the data point should exist outside of any group as well
        prev[key] = { ...newEntry, isGroup: false, values: [] };
        continue;
      }
      if (!(group in taxonomy) || taxonomy[group].type !== TaxonomyEntryType.Group) {
        continue;
      }
      foundGroup = true;
      const groupEntry: IDataPointGroup =
        group in prev
          ? (prev[group] as IDataPointGroup)
          : {
              collapsedValues: [],
              dataPoints: {},
              isGroup: true,
              key: group,
              keyOrder: [],
              label: taxonomy[group].description || group,
              multiple: taxonomy[group].multiple,
              type: TaxonomyEntryType.Group,
              values: [],
            };
      groupEntry.dataPoints[key] = { ...newEntry, isGroup: false };
      prev[group] = groupEntry;
    }
  }
  if (!foundGroup) {
    prev[key] = { ...newEntry, isGroup: false, values: [] };
  }
  return prev;
};
*/

/**
 * Converts a raw data point with a string value to a properly typed value
 * for some of the taxonomy types.
 * @param value IDataPointValue<any> The raw data point value.
 * @param type TaxonomyEntryType The desired target type.
 * @return Returns the normalized data point value against the type.
 */
const normalizeDataPointValue = (
  value: DataPointValue<any>,
  type: TaxonomyEntryType,
): DataPointValue<any> => {
  switch (type) {
    case TaxonomyEntryType.Boolean: {
      let boolValue: boolean | null = null;
      if (value.valueRaw.toLowerCase() === "true") {
        boolValue = true;
      } else if (value.valueRaw.toLowerCase() === "false") {
        boolValue = false;
      }
      return {
        ...value,
        value: boolValue,
      };
    }
    case TaxonomyEntryType.Number: {
      const numberValue = parseFloat(value.valueRaw);
      return {
        ...value,
        value: isNaN(numberValue) ? 0 : numberValue,
      };
    }
    case TaxonomyEntryType.Date:
    case TaxonomyEntryType.DateTime:
    case TaxonomyEntryType.Time:
      return {
        ...value,
        value: moment(value.valueRaw, InnodomDateFormats[type]).toDate(),
      };
    default:
      return value;
  }
};

interface IGenericBlock {
  confidenceClass: ConfidenceClass;
  element: Element;
  height: number;
  page: number;
  references: Array<string>;
  width: number;
  x: number;
  y: number;
  highlights: Array<string>;
}
const extractGenericBlocks = (el: Element): Array<IGenericBlock> => {
  const blocks: Array<IGenericBlock> = [];
  const children = Array.from(el.childNodes);
  for (const block of children) {
    if (!(block instanceof Element) || block.nodeName !== TAG_BLOCK) {
      continue;
    }
    const newBlock: IGenericBlock = {
      confidenceClass: "",
      element: block,
      height:
        parseFloat(block.getAttribute(ATTR_MAX_Y) || "") -
        parseFloat(block.getAttribute(ATTR_MIN_Y) || ""),
      page: parseInt(block.getAttribute(ATTR_PAGE) || "", 10) - 1,
      references: [],
      width:
        parseFloat(block.getAttribute(ATTR_MAX_X) || "") -
        parseFloat(block.getAttribute(ATTR_MIN_X) || ""),
      x: parseFloat(block.getAttribute(ATTR_MIN_X) || ""),
      y: parseFloat(block.getAttribute(ATTR_MIN_Y) || ""),
      highlights: [],
    };
    blocks.push(newBlock);
  }
  return blocks;
};
const extractImageBlocks = (el: Element, id: string): Array<ImageBlock> =>
  extractGenericBlocks(el).map(b => ({
    ...b,
    id,
    text: el.textContent || "",
    type: ContentType.Image,
  }));

const extractMathBlocks = (el: Element, id: string): Array<MathBlock> =>
  extractGenericBlocks(el).map(b => ({
    ...b,
    id,
    text: el.textContent || "",
    type: ContentType.Math,
  }));

export const extractTableCells = (block: IGenericBlock): Array<Array<TableCellBlock>> => {
  const cells: Array<Array<TableCellBlock>> = [];
  const rowElements = Array.from(block.element.getElementsByTagName(TAG_ROW));
  rowElements.forEach((rowEl, rowIndex) => {
    const cellRows: Array<TableCellBlock> = [];
    let row = parseInt(rowEl.getAttribute(ATTR_ROW) || "", 10);
    if (isNaN(row)) {
      row = rowIndex;
    }
    const cellElements = Array.from(rowEl.getElementsByTagName(TAG_COL));
    cellElements.forEach((cellEl, cellIndex) => {
      let col = parseInt(cellEl.getAttribute(ATTR_COL) || "", 10);
      if (isNaN(col)) {
        col = cellIndex;
      }
      let colSpan = parseInt(cellEl.getAttribute(ATTR_COLSPAN) || "", 10);
      if (isNaN(colSpan)) {
        colSpan = 1;
      }
      let rowSpan = parseInt(cellEl.getAttribute(ATTR_ROWSPAN) || "", 10);
      if (isNaN(rowSpan)) {
        rowSpan = 1;
      }
      cellRows.push({
        col,
        colSpan,
        confidenceClass: "",
        element: cellEl,
        height:
          parseFloat(cellEl.getAttribute(ATTR_MAX_Y) || "") -
          parseFloat(cellEl.getAttribute(ATTR_MIN_Y) || ""),
        id: cellEl.getAttribute(ATTR_ID) || "",
        page: block.page,
        references: [],
        row,
        rowSpan,
        text: cellEl.textContent || "",
        type: ContentType.TableCell,
        width:
          parseFloat(cellEl.getAttribute(ATTR_MAX_X) || "") -
          parseFloat(cellEl.getAttribute(ATTR_MIN_X) || ""),
        x: parseFloat(cellEl.getAttribute(ATTR_MIN_X) || ""),
        y: parseFloat(cellEl.getAttribute(ATTR_MIN_Y) || ""),
        highlights: [],
      });
    });
    cells.push(cellRows);
  });
  return cells;
};
const extractTableBlocks = (el: Element, id: string): Array<TableBlock | TableCellBlock> => {
  const tableBlocks = extractGenericBlocks(el).map(
    (b): TableBlock => ({
      ...b,
      cells: [],
      id,
      text: el.textContent || "",
      type: ContentType.Table,
    }),
  );
  const blocks: Array<TableBlock | TableCellBlock> = [];
  for (const tableBlock of tableBlocks) {
    const cells = extractTableCells(tableBlock);
    for (const row of cells) {
      for (const cell of row) {
        blocks.push(cell);
      }
    }
    blocks.push({ ...tableBlock, cells: cells.map(row => row.map(r => r.id)) });
  }
  return blocks;
};
const extractTextBlocks = (el: Element, id: string): Array<TextBlock> =>
  extractGenericBlocks(el).map(b => ({
    ...b,
    id,
    text: el.textContent || "",
    type: ContentType.Text,
    highlights: [],
  }));

const extractContent = (
  contentEl: Element,
  pages: State["pages"],
): [State["blocks"], State["pages"]] => {
  const blocks: State["blocks"] = {};
  const contentChildren = Array.from(contentEl.childNodes);
  for (const child of contentChildren) {
    if (!(child instanceof Element)) {
      continue;
    }
    // ensure each content node has a unique ID
    const blockId = child.getAttribute(ATTR_ID);
    if (!blockId || blockId in blocks) {
      continue;
    }
    let currentBlocks: Array<Block> | undefined;
    switch (child.nodeName) {
      case TAG_IMAGE: {
        currentBlocks = extractImageBlocks(child, blockId);
        break;
      }
      case TAG_MATH: {
        currentBlocks = extractMathBlocks(child, blockId);
        break;
      }
      case TAG_TABLE: {
        currentBlocks = extractTableBlocks(child, blockId);
        break;
      }
      case TAG_TEXT: {
        currentBlocks = extractTextBlocks(child, blockId);
        break;
      }
      default: {
        console.error("Unrecognized content tag:", child.nodeName);
        continue;
      }
    }
    if (currentBlocks) {
      for (const block of currentBlocks) {
        blocks[block.id] = block;
        if (pages[block.page] === undefined) {
          pages[block.page] = { blocks: [] };
        }
        pages[block.page].blocks.push(block.id);
      }
    }
  }
  return [blocks, pages];
};

const generateEmptyValues = (
  taxonomy: TaxonomyEntry[],
  rawValues: State["values"],
): State["values"] => {
  const toReturn: State["values"] = {};
  
  for (const rootEntry of taxonomy) {
    if (rootEntry.type === TaxonomyEntryType.Group) {
      if (rootEntry.multiple) {
        const uniqueGroupIndices = Object.keys(
          Object.keys(rawValues).reduce((prev: { [index: string]: true }, next) => {
            if (!next.toLocaleLowerCase().startsWith(`${rootEntry.id}.`.toLocaleLowerCase())) {
              return prev;
            }
            const split = next.toLocaleLowerCase().split(".");
            prev[split[1]] = true;
            return prev;
          }, {}),
        );
        if (uniqueGroupIndices.length < 1) {
          uniqueGroupIndices.push("1");
        }
        for (const groupIndex of uniqueGroupIndices) {
          for (const dataPoint of rootEntry.children) {
            const dataPointEntry = dataPoint as NonGroupTaxonomyEntry;
            const dataPointKey = `${rootEntry.id}.${groupIndex}.${dataPoint.id}`.toLocaleLowerCase();
            if (dataPointEntry.multiple) {
              const valueExists = Object.keys(rawValues).some(k =>
                k.startsWith(`${dataPointKey}.`),
              );
              if (!valueExists) {
                const newKey = `${dataPointKey}.1`;
                toReturn[newKey] = newDefaultValue(dataPoint.id, newKey);
              }
            } else {
              if (!(dataPointKey in rawValues)) {
                toReturn[dataPointKey] = newDefaultValue(dataPoint.id, dataPointKey);
              }
            }
          }
        }
      } else {
        for (const dataPoint of rootEntry.children) {
          const dataPointEntry = dataPoint as NonGroupTaxonomyEntry;
          const dataPointKey = `${rootEntry.id}.${dataPoint.id}`.toLocaleLowerCase();
          if (dataPointEntry.multiple) {
            const valueExists = Object.keys(rawValues).some(k => k.toLocaleLowerCase().startsWith(`${dataPointKey}.`));
            if (!valueExists) {
              const newKey = `${dataPointKey}.1`;
              toReturn[newKey] = newDefaultValue(dataPoint.id, newKey);
            }
          } else {
            if (!(dataPointKey in rawValues)) {
              toReturn[dataPointKey] = newDefaultValue(dataPoint.id, dataPointKey);
            }
          }
        }
      }
    } else {
      if (rootEntry.multiple) {
        const valueExists = Object.keys(rawValues).some(k => k.toLocaleLowerCase().startsWith(`${rootEntry.id}.`.toLocaleLowerCase()));
        if (!valueExists) {
          const newKey = `${rootEntry.id}.1`;
          toReturn[newKey.toLocaleLowerCase()] = newDefaultValue(rootEntry.id, newKey);
        }
      } else if (!(rootEntry.id in rawValues)) {
        toReturn[rootEntry.id.toLocaleLowerCase()] = newDefaultValue(rootEntry.id, rootEntry.id);
      }
    }
  }
  return toReturn;
};

export const normalizeValues = (
  taxonomy: TaxonomyEntry[],
  rawValues: State["values"],
): State["values"] => {
  // populate missing values with empty fields
  const populatedValues = {
    ...generateEmptyValues(taxonomy, rawValues),
    ...rawValues,
  };
  const values: State["values"] = {};
  for (const key of Object.keys(populatedValues)) {
    
    values[key] = normalizeDataPointValue(
      populatedValues[key],
      extractKeysAndIndexes(key, taxonomy).taxonomyEntry.type,
    );
  }
  return values;
};

/**
 * For the given <inno:metadata> element and taxonomy, builds the entire data point models.
 * @param meta Element The <inno:metadata> element.
 * @param taxonomy ITaxonomy The input taxonomy.
 * @return State["dataPoints"] Returns a data points model ready for the state.
 */
const extractValues = (
  meta: Element,
  taxonomy: TaxonomyEntry[],
  rawBlocks: State["blocks"],
  confidenceThreshold: number,
  xmlDoc: Document,
): [State["blocks"], State["values"]] => {
  const [extractedBlocks, rawValues] = mapNodesToDataPoints(meta, taxonomy, rawBlocks, xmlDoc);
  const values = normalizeValues(taxonomy, rawValues);
  // add confidence classes
  const blocks = Object.keys(extractedBlocks).reduce((prev, next) => {
    prev[next] = {
      ...prev[next],
      confidenceClass: getConfidenceClass(prev[next], confidenceThreshold, rawValues),
      highlights: GetHighlightedText(prev[next], taxonomy, rawValues),
    };
    return prev;
  }, extractedBlocks);
  return [blocks, values];
};



export const getDefaultState = (
  taxonomy: WorkbenchTaxonomy,
  xml: string,
  rawPages: State["pages"],
  confidenceThreshold: number,
  authToken: string
): State => {

  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xml, "text/xml");
  const errors = xmlDoc.getElementsByTagName("parsererror");
 
  if (errors.length > 0) {
    throw new Error(`Error parsing XML: ${new XMLSerializer().serializeToString(xmlDoc)}`);
  }
  const { documentElement } = xmlDoc;
  if (!documentElement || !(documentElement instanceof Element)) {
    throw new Error("Invalid or empty XML.");
  }
  const contentTags = documentElement.getElementsByTagName(TAG_CONTENT);
  let rawBlocks: State["blocks"] = {};
  let pages: State["pages"] = [];
  if (contentTags.length > 0) {
    const content = extractContent(contentTags[0], rawPages);
    rawBlocks = content[0];
    pages = content[1];
  }
  let blocks: State["blocks"] = rawBlocks;
  let values: State["values"] = {};
  const metadataTags = documentElement.getElementsByTagName(TAG_META);
  if (metadataTags.length > 0) {
    const extractedValues = extractValues(
      metadataTags[0],
      taxonomy.taxonomyObject,
      rawBlocks,
      confidenceThreshold,
      xmlDoc,
    );
    blocks = extractedValues[0];
    values = extractedValues[1];
  }

  return {
    activeValue: "",
    blocks,
    confidenceThreshold,
    modified: false,
    pages,
    scrollToBlock: "",
    taxonomy: taxonomy,
    values,
    authToken
  };
};
